import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Modal } from 'antd'
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import fetchApi from '../../../../../../lib/utils/fetch-api'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import './style.css'

interface Requisite {
  id?: number | null
  name?: string
  active?: boolean
  address?: string
  director?: string
  inn?: string
  invoice_curr?: string
  invoice_corr?: string
  bank_name?: string
  bik?: string
  okpo?: string
  kpp?: string
  ogrn?: string
}

interface ExistingEmployeeBySnils {
  id: number
  fio: string | null
  phone: string | null
}

type SnilsIssue =
  | { type: 'placeholder'; message: string }
  | { type: 'conflict'; message: string; employee: ExistingEmployeeBySnils }
  | { type: 'check_failed'; message: string }

const selectSession = createSelector(
  (state: any) => state.session,
  session => session,
)

interface HrModalProps {
  isOpen: boolean
  onOk?: ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined
  onCancel?: ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined
  title?: string
  centered?: boolean
  formData: any
  schema: any
  onChange: any
  disabled?: boolean
}

const SNILS_CHECK_DEBOUNCE_MS = 350

function snilsDigits(value: unknown): string {
  return String(value ?? '').replace(/\D/g, '')
}

function isPlaceholderSnils(digits: string): boolean {
  return digits.length === 11 && /^(\d)\1{10}$/.test(digits)
}

export const HrModal: React.FC<HrModalProps> = memo((props: HrModalProps) => {
  const {
    isOpen,
    onCancel,
    onOk,
    formData,
    schema,
    onChange,
    title,
    disabled,
    centered,
    ...otherProps
  } = props
  void centered

  const { sessionData } = useSelector(selectSession)
  const officeId = sessionData?.user?.office?.id
  const [requisites, setRequisites] = useState<Requisite[]>([])
  const [localFormData, setLocalFormData] = useState<any>(formData)
  const [snilsConflict, setSnilsConflict] = useState<ExistingEmployeeBySnils | null>(null)
  const [snilsChecking, setSnilsChecking] = useState(false)
  const [snilsVerifiedFree, setSnilsVerifiedFree] = useState<string | null>(null)
  const [snilsCheckFailed, setSnilsCheckFailed] = useState(false)
  const [snilsRetryTick, setSnilsRetryTick] = useState(0)
  const snilsCheckSeq = useRef(0)

  const hasSnilsField = Boolean(schema?.properties?.snils)
  const digits = snilsDigits(localFormData?.snils)

  const wasOpenRef = useRef(false)

  const resetSnilsCheckState = useCallback(() => {
    setSnilsConflict(null)
    setSnilsChecking(false)
    setSnilsVerifiedFree(null)
    setSnilsCheckFailed(false)
  }, [])

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setLocalFormData(formData)
      resetSnilsCheckState()
    }
    if (!isOpen) {
      resetSnilsCheckState()
    }
    wasOpenRef.current = isOpen
  }, [isOpen, formData, resetSnilsCheckState])

  useEffect(() => {
    const hasCompanyField = Boolean(schema?.properties?.company)
    if (!isOpen || !officeId || !hasCompanyField) {
      return
    }

    fetchApi(`/api/office/${officeId}/requisites`)
      .then((data: any) => setRequisites(Array.isArray(data) ? data : []))
      .catch(() => setRequisites([]))
  }, [isOpen, officeId, schema])

  useEffect(() => {
    if (!isOpen || !hasSnilsField) {
      snilsCheckSeq.current += 1
      resetSnilsCheckState()
      return
    }

    // пусто / неполный / плейсхолдер — сервер не дергаем; бампим seq, чтобы отменить inflight
    if (!digits || digits.length !== 11 || isPlaceholderSnils(digits)) {
      snilsCheckSeq.current += 1
      setSnilsConflict(null)
      setSnilsChecking(false)
      setSnilsVerifiedFree(null)
      setSnilsCheckFailed(false)
      return
    }

    const seq = ++snilsCheckSeq.current
    setSnilsConflict(null)
    setSnilsVerifiedFree(null)
    setSnilsCheckFailed(false)
    setSnilsChecking(true)

    const timer = window.setTimeout(() => {
      fetchApi(`/api/hr/employees/by-snils?snils=${encodeURIComponent(digits)}`, {}, true)
        .then((result: any) => {
          if (seq !== snilsCheckSeq.current) return
          if (result?.error) {
            setSnilsConflict(null)
            setSnilsVerifiedFree(null)
            setSnilsCheckFailed(true)
            return
          }
          const existing = result?.data || null
          setSnilsConflict(existing)
          setSnilsVerifiedFree(existing ? null : digits)
          setSnilsCheckFailed(false)
        })
        .catch(() => {
          if (seq !== snilsCheckSeq.current) return
          setSnilsConflict(null)
          setSnilsVerifiedFree(null)
          setSnilsCheckFailed(true)
        })
        .finally(() => {
          if (seq === snilsCheckSeq.current) {
            setSnilsChecking(false)
          }
        })
    }, SNILS_CHECK_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [isOpen, hasSnilsField, digits, snilsRetryTick, resetSnilsCheckState])

  const snilsIssue: SnilsIssue | null = useMemo(() => {
    if (!hasSnilsField || !isOpen) return null
    if (!digits) return null

    if (digits.length === 11 && isPlaceholderSnils(digits)) {
      return {
        type: 'placeholder',
        message:
          'Некорректный СНИЛС (одинаковые цифры). Укажите действительный номер или оставьте поле пустым',
      }
    }

    if (snilsConflict) {
      const parts = [
        `ID ${snilsConflict.id}`,
        snilsConflict.fio || null,
        snilsConflict.phone ? `тел. ${snilsConflict.phone}` : null,
      ].filter(Boolean)
      return {
        type: 'conflict',
        message: `Сотрудник с таким СНИЛС уже есть: ${parts.join(', ')}`,
        employee: snilsConflict,
      }
    }

    if (snilsCheckFailed && digits.length === 11) {
      return {
        type: 'check_failed',
        message: 'Не удалось проверить СНИЛС. Измените номер или попробуйте ещё раз',
      }
    }

    return null
  }, [hasSnilsField, isOpen, digits, snilsConflict, snilsCheckFailed])

  const snilsIncomplete = Boolean(digits) && digits.length > 0 && digits.length < 11
  const snilsOk = Boolean(
    digits.length === 11 &&
      !isPlaceholderSnils(digits) &&
      !snilsChecking &&
      !snilsConflict &&
      !snilsCheckFailed &&
      snilsVerifiedFree === digits,
  )

  const handleOk = (e: React.MouseEvent<HTMLElement>) => {
    if (snilsIssue || snilsChecking || snilsIncomplete) return
    if (digits.length === 11 && snilsVerifiedFree !== digits) return
    onOk?.(e)
  }

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    onCancel?.(e)
  }

  const schemaWithExtras = useMemo(() => {
    let nextSchema = schema

    if (schema?.properties?.company && requisites.length > 0) {
      const activeRequisites = requisites.filter(r => r?.active !== false)
      const oneOf = activeRequisites
        .filter(r => r?.id != null && r?.name)
        .map(r => ({
          const: r.id,
          title: r.name as string,
        }))

      if (oneOf.length > 0) {
        nextSchema = {
          ...nextSchema,
          properties: {
            ...nextSchema.properties,
            company: {
              ...nextSchema.properties.company,
              oneOf,
              enum: oneOf.map(x => x.const),
              view: { name: 'select-from-enum' },
              options: { ...(nextSchema.properties.company?.options || {}), showSearch: true },
            },
          },
        }
      }
    }

    if (nextSchema?.properties?.snils) {
      const snilsOptions = { ...(nextSchema.properties.snils.options || {}) }
      delete snilsOptions.status
      delete snilsOptions.inputDisabled
      // не options.disabled — иначе Typography вместо Input
      delete snilsOptions.disabled

      if (snilsChecking) {
        snilsOptions.inputDisabled = true
      } else if (snilsIssue) {
        snilsOptions.status = 'error'
      } else if (snilsOk) {
        snilsOptions.status = 'success'
      }

      nextSchema = {
        ...nextSchema,
        properties: {
          ...nextSchema.properties,
          snils: {
            ...nextSchema.properties.snils,
            options: snilsOptions,
          },
        },
      }
    }

    return nextSchema
  }, [schema, requisites, snilsChecking, snilsIssue, snilsOk])

  const handleChange = useCallback(
    (params: any) => {
      const nextFormData = params?.formData
      const companyId = nextFormData?.company
      const selected = requisites.find(r => r?.id != null && r.id === companyId)

      const mergedFormData = selected
        ? { ...nextFormData, company_requisites: selected }
        : nextFormData

      setLocalFormData(mergedFormData)
      onChange?.({ ...params, formData: mergedFormData })
    },
    [onChange, requisites],
  )

  // InputText debounce 1s — сразу обновляем local + parent formRef
  const handleSnilsInputCapture = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      if (!hasSnilsField) return
      const target = e.target as HTMLInputElement | null
      if (!target || target.id !== 'snils') return
      const nextSnils = target.value

      setLocalFormData((prev: any) => {
        if (prev?.snils === nextSnils) return prev
        const merged = { ...prev, snils: nextSnils }
        onChange?.({ formData: merged })
        return merged
      })
    },
    [hasSnilsField, onChange],
  )

  const needsSnilsGate =
    hasSnilsField &&
    (snilsChecking ||
      Boolean(snilsIssue) ||
      snilsIncomplete ||
      (digits.length === 11 && snilsVerifiedFree !== digits))

  const okDisabled = Boolean(disabled) || needsSnilsGate

  const snilsStatusAlert = useMemo(() => {
    if (!hasSnilsField) return null

    if (snilsChecking) {
      return (
        <Alert
          type="info"
          showIcon
          icon={<LoadingOutlined spin />}
          message="Проверяем СНИЛС…"
          description="Ищем, нет ли уже сотрудника с таким номером"
          style={{ marginBottom: 12, textAlign: 'left' }}
        />
      )
    }

    if (snilsIssue) {
      return (
        <Alert
          type="error"
          showIcon
          message={snilsIssue.message}
          style={{ marginBottom: 12, textAlign: 'left' }}
          action={
            snilsIssue.type === 'check_failed' ? (
              <a
                href="#retry-snils"
                onClick={e => {
                  e.preventDefault()
                  setSnilsCheckFailed(false)
                  setSnilsRetryTick(t => t + 1)
                }}
              >
                Повторить
              </a>
            ) : undefined
          }
        />
      )
    }

    if (snilsIncomplete) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Введите все 11 цифр СНИЛС"
          description="Или очистите поле, если СНИЛС пока неизвестен"
          style={{ marginBottom: 12, textAlign: 'left' }}
        />
      )
    }

    if (snilsOk) {
      return (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          message="СНИЛС свободен — можно оформлять"
          style={{ marginBottom: 12, textAlign: 'left' }}
        />
      )
    }

    return null
  }, [hasSnilsField, snilsChecking, snilsIssue, snilsIncomplete, snilsOk])

  if (!localFormData) {
    return null
  }

  return (
    <Modal
      title={title}
      onOk={handleOk}
      onCancel={handleCancel}
      open={isOpen}
      destroyOnHidden={true}
      centered={false}
      width={720}
      wrapClassName="hr-modal-snils"
      style={{ top: 0, maxWidth: '100vw' }}
      styles={{
        wrapper: { overflow: 'hidden' },
        content: {
          height: '100vh',
          maxHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 10,
        },
        header: {
          flexShrink: 0,
          marginBottom: 0,
          padding: '0 0 15px',
          borderBottom: '1px solid #f0f0f0',
        },
        body: {
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '15px 10px 15px 0px',
        },
        footer: {
          flexShrink: 0,
          marginTop: 0,
          padding: '15px 0 0',
          borderTop: '1px solid #f0f0f0',
        },
      }}
      okButtonProps={{
        disabled: okDisabled,
        loading: snilsChecking,
      }}
      {...otherProps}
      footer={(_, { OkBtn, CancelBtn }) => (
        <div>
          {snilsStatusAlert}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <CancelBtn />
            <OkBtn />
          </div>
        </div>
      )}
    >
      <div onInput={handleSnilsInputCapture}>
        <DefaultJsonForm
          formData={localFormData}
          schema={schemaWithExtras}
          onChange={handleChange}
          orientation="horizontal"
        />
      </div>
    </Modal>
  )
})

HrModal.displayName = 'HrModal'
